import { bigCommerceFetch } from "."
import { pushErrors } from "./errors"
import { jwtDecode } from "jwt-decode"
import { registerCustomerMutation } from "./mutations/customer"
import { api } from "../../../../api"
import { login, newNotification } from "../../../store"
import { getQueryParam } from "../../utils"

export async function registerCustomer(customerDetails: RegisterCustomerInput): Promise<BigCommerceCustomer | null> {
    const res = await bigCommerceFetch<BigCommerceRegisterCustomerOperation>({
        query: registerCustomerMutation,
        variables: {
            registerCustomerInput: customerDetails,
        },
        cache: "no-store",
    })

    const bigCommerceCustomer = res.body.data.customer.registerCustomer.customer
    if (!bigCommerceCustomer) {
        const errors = res.body.data.customer.registerCustomer.errors
        if (errors) pushErrors(errors)

        return null
    }
    return bigCommerceCustomer
}

export async function requestResetPassword(resetPasswordDetails: RequestResetPasswordInput): Promise<boolean> {
    await api(
        "action",
        {
            method: "POST",
        },
        {
            operation: "requestPasswordReset",
            ...resetPasswordDetails,
        }
    )
    return true
}

export async function resetPassword(resetPasswordDetails: ResetPasswordInput): Promise<boolean> {
    const token = getQueryParam("token")
    if (!token) {
        newNotification({
            class: "error",
            html: "Kein Token gefunden",
        })
        throw new Error("Kein Token gefunden")
    }
    const tokenDecoded = jwtDecode<JWTPwResetClaims>(token)
    const customerTibiId = tokenDecoded?.tibiId
    if (!customerTibiId) {
        newNotification({
            class: "error",
            html: "Keine Kunden-ID im token gefunden",
        })
        throw new Error("Keine Kunden-ID im token gefunden")
    }
    await api(
        `bigCommerceCustomer/${customerTibiId}`,
        {
            method: "PUT",
            useJwt: true,
            jwtToUse: token,
        },
        {
            operation: "resetPassword",
            ...resetPasswordDetails,
        }
    )
    return true
}

export async function changePassword(customerTibiId: string, currentPassword: string, newPassword: string) {
    const res = await api(
        `bigCommerceCustomer/${customerTibiId}`,
        {
            method: "PUT",
            useJwt: true,
        },
        {
            operation: "changePassword",
            currentPassword,
            newPassword,
        }
    )
    if (res.data.status === 200) {
        newNotification({
            class: "success",
            html: "Passwort wurde erfolgreich geändert.",
        })
        return true
    } else {
        newNotification({
            class: "error",
            html: "Fehler! Bitte prüfe deine Eingabe.",
        })
        return false
    }
}

export async function changeEmail(customerTibiId: string, email: string, password: string) {
    const res = await api(
        `bigCommerceCustomer/${customerTibiId}`,
        {
            method: "PUT",
            useJwt: true,
        },
        {
            operation: "updateEmail",
            email,
            password,
        }
    )
    if (res.data.status === 200) {
        newNotification({
            class: "success",
            html: "E-Mail wurde erfolgreich geändert.",
        })
        return true
    } else if (res.data.status === 409) {
        newNotification({
            class: "error",
            html: "E-Mail Adresse bereits vergeben.",
        })
        return false
    } else if (res.data.status === 403) {
        newNotification({
            class: "error",
            html: "Passwort falsch.",
        })
        return false
    } else {
        newNotification({
            class: "error",
            html: "Fehler! Bitte prüfe deine Eingabe.",
        })
        return false
    }
}

export async function postLogin(loginDetails: LoginInput): Promise<Login> {
    const r = await api<ServerLogin>(
        "login",
        {
            method: "POST",
        },
        loginDetails
    )

    if (r.data?.token) {
        const _l: Login = {
            created: new Date(r.data.created),
            tokenString: r.data.token,
            tokenData: jwtDecode<JWTLoginClaims>(r.data.token),
            customer: r.data.customer,
        }
        login.set(_l)

        return _l
    }
    throw new Error("token not found")
}

export async function postLogout() {
    const r = await api<ServerLogin>(
        "login?logout=true",
        {
            method: "POST",
        },
        {}
    )
    login.set(null)
}

export async function getCustomer(customerEntityId: string): Promise<BigCommerceCustomer> {
    const res = await api<BigCommerceCustomer>(`bigCommerceCustomer/${customerEntityId}`, {
        method: "GET",
        useJwt: true,
    })
    console.log("customer", res.data)
    return res.data.data
}

export async function updateCustomer(
    customerEntityId: string,
    customer: Partial<BigCommerceCustomer>
): Promise<BigCommerceCustomer> {
    const res = await api<BigCommerceCustomer>(
        `bigCommerceCustomer/${customerEntityId}`,
        {
            method: "PUT",
            useJwt: true,
        },
        { customer: customer, operation: "updateCustomer" }
    )
    if (res.data.status === 200) {
        newNotification({
            class: "success",
            html: "Kundendaten wurden erfolgreich aktualisiert.",
        })
    } else {
        newNotification({
            class: "error",
            html: "Kundendaten konnten nicht aktualisiert werden.",
        })
    }

    return res.data.data
}

export async function updateCustomerAddress(
    customerEntityId: string,
    address: BigCommerceAddress
): Promise<BigCommerceAddress> {
    const res = await api<BigCommerceAddress>(
        `bigCommerceCustomer/${customerEntityId}`,
        {
            method: "PUT",
            useJwt: true,
        },
        { address: address, operation: "updateCustomerAddress" }
    ).catch((e) => {
        newNotification({
            class: "error",
            html: "Adresse konnte nicht aktualisiert werden.",
        })
    })
    if (res.data.status === 200) {
        newNotification({
            class: "success",
            html: "Adresse wurde erfolgreich aktualisiert.",
        })
    } else {
        newNotification({
            class: "error",
            html: "Adresse konnte nicht aktualisiert werden.",
        })
    }

    return res.data.data
}

export async function deleteCustomerAddress(customerEntityId: string, addressId: number): Promise<boolean> {
    const res = await api<null>(
        `bigCommerceCustomer/${customerEntityId}`,
        {
            method: "PUT",
            useJwt: true,
        },
        { addressId: addressId, operation: "deleteCustomerAddress" }
    )
    if (res.data.status === 200) {
        newNotification({
            class: "success",
            html: "Adresse wurde erfolgreich gelöscht.",
        })
        return true
    } else
        newNotification({
            class: "error",
            html: "Adresse konnte nicht gelöscht werden.",
        })
    return false
}

export async function addCustomerAddress(
    customerEntityId: string,
    address: BigCommerceAddress
): Promise<BigCommerceAddress> {
    const res = await api<BigCommerceCustomer>(
        `bigCommerceCustomer/${customerEntityId}`,
        {
            method: "PUT",
            useJwt: true,
        },
        { address: address, operation: "addCustomerAddress" }
    )
    if (res.data.status === 200) {
        newNotification({
            class: "success",
            html: "Adresse wurde erfolgreich hinzugefügt.",
        })
    } else {
        newNotification({
            class: "error",
            html: "Adresse konnte nicht hinzugefügt werden.",
        })
    }

    return res.data.data
}

export async function getCustomerAddress(
    customerEntityId: string,
    customerAddressId: number
): Promise<BigCommerceAddress> {
    const res = await api<BigCommerceCustomer>(`bigCommerceCustomer/${customerEntityId}?address=${customerAddressId}`, {
        method: "GET",
        useJwt: true,
    })
    return res.data.data
}

export async function getCustomerAddresses(customerEntityId: string): Promise<BigCommerceAddress[]> {
    const res = await api<BigCommerceCustomer>(`bigCommerceCustomer/${customerEntityId}?addresses=true`, {
        method: "GET",
        useJwt: true,
    })
    return res.data.data
}
